import { memo, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Grid } from "@material-ui/core";
import { FormNewSelect } from "@chhjit/react-components";

import { TileSelect } from "../../../ui/form-fields/tile-select";
import {
  TallHouseIcon,
  AtticIcon,
  BuildingIcon,
  StorageIcon,
} from "../../../assets";

function ordinalSuffix(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const DWELLING_TYPE_OPTIONS: {
  value: string;
  label: string;
  icon: JSX.Element;
}[] = [
  { value: "2", label: "Apartment/condo", icon: <TallHouseIcon /> },
  { value: "1", label: "House", icon: <AtticIcon /> },
  { value: "6", label: "Business", icon: <BuildingIcon /> },
  { value: "5", label: "Storage Unit", icon: <StorageIcon /> },
];

const FLOOR_OPTIONS: Record<string, { value: string; label: string }[]> = {
  // Apartment/condo
  "2": Array.from({ length: 50 }, (_, i) => ({
    value: String(i + 1),
    label: `${ordinalSuffix(i + 1)} floor`,
  })),
  // House
  "1": [
    { value: "1", label: "1 Story" },
    { value: "2", label: "2 Stories" },
    { value: "3", label: "3 Stories" },
    { value: "4", label: "4 Stories" },
  ],
  // Business
  "6": Array.from({ length: 20 }, (_, i) => ({
    value: String(i + 1),
    label: i === 0 ? "Ground floor" : `${ordinalSuffix(i + 1)} floor`,
  })),
  // Storage Unit
  "5": [
    { value: "1", label: "Ground level" },
    { value: "2", label: "2nd level" },
  ],
};

const ACCESS_TYPE_OPTIONS = [
  { value: "2", label: "Use Stairs" },
  { value: "3", label: "Use Elevator" },
];

type DwellingInfoBlockProps = {
  control: any;
  source: "origin" | "destination";
};

/**
 * Self-contained dwelling information block.
 *
 * Renders dwelling-type tile-select + floor + access-type as a single unit.
 * Keeping them together prevents the floor picker (whose options depend on
 * the selected dwelling type) from being split across steps.
 */
export const DwellingInfoBlock = memo(
  ({ control, source }: DwellingInfoBlockProps) => {
    const { setValue } = useFormContext();

    // Derive field names from source prefix
    const fieldTypeOfDwelling = `${source}TypeOfDwelling`;
    const fieldFloor = `${source}Floor`;
    const fieldAccessType = `${source}AccessType`;

    const typeOfDwelling = useWatch({ control, name: fieldTypeOfDwelling });
    const floor = useWatch({ control, name: fieldFloor });
    const accessType = useWatch({ control, name: fieldAccessType });

    // Normalize: TileSelect stores plain strings; FormNewSelect stores { value, label }.
    const dwellingStr =
      typeof typeOfDwelling === "object" && typeOfDwelling !== null
        ? (typeOfDwelling as any).value
        : String(typeOfDwelling ?? "");

    const floorStr =
      typeof floor === "object" && floor !== null
        ? (floor as any).value
        : String(floor ?? "");

    const accessTypeStr =
      typeof accessType === "object" && accessType !== null
        ? (accessType as any).value
        : String(accessType ?? "");

    const floorOptions = FLOOR_OPTIONS[dwellingStr] ?? FLOOR_OPTIONS["2"];

    // Show stairs/elevator only when floor is selected AND it's 2nd floor or higher.
    const floorNum = parseInt(floorStr, 10);
    const showAccessType = !!floorStr && !isNaN(floorNum) && floorNum > 1;

    // Sentinel logic: "__none__" satisfies Yup required when accessType is hidden.
    useEffect(() => {
      if (!showAccessType) {
        if (accessTypeStr !== "__none__") {
          setValue(fieldAccessType, "__none__", { shouldValidate: false });
        }
      } else if (accessTypeStr === "__none__") {
        setValue(fieldAccessType, "", { shouldValidate: false });
      }
    }, [showAccessType, accessTypeStr, setValue, fieldAccessType]);

    // Reset floor + accessType when dwelling type changes (skip initial mount).
    const hasInitialized = useRef(false);
    useEffect(() => {
      if (!hasInitialized.current) {
        hasInitialized.current = true;
        return;
      }
      const dwStr =
        typeof typeOfDwelling === "object" && typeOfDwelling !== null
          ? (typeOfDwelling as any).value
          : String(typeOfDwelling ?? "");
      const opts = FLOOR_OPTIONS[dwStr] ?? FLOOR_OPTIONS["2"];
      setValue(fieldFloor, opts[0] ?? "", { shouldValidate: false });
      setValue(fieldAccessType, "__none__", { shouldValidate: false });
    }, [typeOfDwelling, setValue, fieldFloor, fieldAccessType]); // eslint-disable-line react-hooks/exhaustive-deps

    // Mount-only: convert plain string floor/accessType from store readTransform
    // to { value, label } objects that FormNewSelect requires.
    useEffect(() => {
      if (dwellingStr === "") return;
      if (typeof floor === "string" && floor !== "" && floor !== "__none__") {
        const opt = floorOptions.find((o) => o.value === floor);
        if (opt) setValue(fieldFloor, opt, { shouldValidate: false });
      }
      if (
        showAccessType &&
        typeof accessType === "string" &&
        accessType !== "" &&
        accessType !== "__none__"
      ) {
        const opt = ACCESS_TYPE_OPTIONS.find((o) => o.value === accessType);
        if (opt) setValue(fieldAccessType, opt, { shouldValidate: false });
      }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <>
        <Grid item xs={12}>
          <TileSelect
            control={control}
            name={fieldTypeOfDwelling}
            options={DWELLING_TYPE_OPTIONS}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FormNewSelect
            control={control}
            name={fieldFloor}
            label="Other details"
            options={floorOptions}
            required
            menuPortalTarget={document.body}
          />
        </Grid>
        {showAccessType && (
          <Grid item xs={12}>
            <FormNewSelect
              control={control}
              name={fieldAccessType}
              label="Stairs or elevator?"
              options={ACCESS_TYPE_OPTIONS}
              required
              menuPortalTarget={document.body}
            />
          </Grid>
        )}
      </>
    );
  },
);
